// src/pages/UserProfile.js
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Sidebar from '../components/Sidebar';
import RoleBadge from '../components/RoleBadge';
import { getUserProfile, getOrganization } from '../Api';
import { getOrganizationId, notifyAuthDataUpdated } from '../utils/authUtils';
import './UserProfile.css';

const UserProfile = () => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfileData();
  }, []);

  const loadProfileData = async () => {
    try {
      setLoading(true);

      // Fetch user profile
      const userData = await getUserProfile();
      setUser(userData);
      if (typeof window !== 'undefined') {
        try {
          const storage = window.localStorage;
          if (Array.isArray(userData?.roles) && userData.roles.length > 0) {
            storage.setItem('userRoles', JSON.stringify(userData.roles));
          } else {
            storage.removeItem('userRoles');
          }
          notifyAuthDataUpdated({ invalidateMetadata: true });
        } catch (syncError) {
          console.warn('[UserProfile] Failed to sync roles to localStorage:', syncError);
        }
      }

      // Fetch organization details
      const orgId = getOrganizationId();
      if (orgId) {
        const orgData = await getOrganization(orgId);
        setOrganization(orgData);
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <div className="app-container">
        <Sidebar />
        <div className="main-section">
          <div className="profile-loading">
            <div className="loading-spinner"></div>
            <p>Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Sidebar />
      <div className="main-section">
        <div className="profile-page-container">
          
          {/* Modern Minimal Header */}
          <div className="profile-header-minimal">
            <div className="profile-avatar-wrapper">
              <div className="profile-avatar">
                {user?.first_name?.charAt(0) || 'U'}
              </div>
              <div className="profile-status-indicator"></div>
            </div>
            <div className="profile-identity-minimal">
              <h1 className="profile-name">
                {user?.first_name} {user?.last_name}
              </h1>
              <p className="profile-email">{user?.email}</p>
              <div className="profile-badges">
                {user?.is_active ? (
                  <span className="status-badge status-active">
                    Active Account
                  </span>
                ) : (
                  <span className="status-badge status-inactive">
                    Inactive
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="profile-content-grid">
            {/* Personal Details */}
            <div className="info-card">
              <div className="card-header">
                <h3>Personal Information</h3>
              </div>
              <div className="card-content">
                <div className="info-row">
                  <div className="info-group">
                    <label>Full Name</label>
                    <div className="info-value">{user?.first_name} {user?.last_name}</div>
                  </div>
                  <div className="info-group">
                    <label>Email Address</label>
                    <div className="info-value">{user?.email}</div>
                  </div>
                </div>
                <div className="info-row">
                  <div className="info-group">
                    <label>Account Created</label>
                    <div className="info-value">{formatDate(user?.created_at)}</div>
                  </div>
                  <div className="info-group">
                    <label>Last Login</label>
                    <div className="info-value">{formatDate(user?.last_login)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Organization Details */}
            <div className="info-card">
              <div className="card-header">
                <h3>Organization</h3>
              </div>
              <div className="card-content">
                <div className="info-group">
                  <label>Organization Name</label>
                  <div className="info-value highlight-value">{organization?.name || 'N/A'}</div>
                </div>
                <div className="info-group">
                  <label>Organization ID</label>
                  <div className="info-value code-value">
                    {organization?.organization_id || 'N/A'}
                  </div>
                </div>
                <div className="info-group">
                  <label>Member Since</label>
                  <div className="info-value">{formatDate(organization?.created_at)}</div>
                </div>
              </div>
            </div>

            {/* Roles */}
            <div className="info-card">
              <div className="card-header">
                <h3>Roles & Permissions</h3>
              </div>
              <div className="card-content">
                <div className="roles-container">
                  {user?.roles && user.roles.length > 0 ? (
                    user.roles.map((roleId) => (
                      <div key={roleId} className="role-badge-wrapper">
                        <RoleBadge roleId={roleId} />
                      </div>
                    ))
                  ) : (
                    <span className="no-roles-text">No roles assigned</span>
                  )}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default UserProfile;
